import { css, Inline, styled, useModeContext } from 'canoe';
import React from 'react';

import {
  PositionSelector,
  PositionSelectorButton,
  PositionSelectorColumn,
  PositionSelectorWrapper,
  ValueButton,
  ValueGroup,
} from '@candu-ai/craft/ui';
import { Section } from 'craft/containers/Toolbox/components';
import { useMultiNodes } from 'craft/hooks';
import { TooltipPosition as Position } from 'craft/resolvers/Hotspot/types';
import { getDomNodeFromDocument } from 'craft/utils/viewport';

import { TourStepZindex } from './TourStepZindex';

const CenterDot = styled.div`
  ${({ theme }) => css`
    width: 10px;
    height: 10px;
    border-radius: 100%;
    background-color: ${theme.colors.GlitterGrayD20};
  `}
`;

interface SelectorProps {
  tooltipPosition: Position;
  setPosition: (newPosition: Position) => void;
}

const Selector = ({ tooltipPosition, setPosition }: SelectorProps) => {
  const { mode } = useModeContext();

  return (
    <PositionSelectorWrapper mode={mode}>
      <PositionSelectorButton
        isSelected={tooltipPosition === Position.Left}
        onClick={() => setPosition(Position.Left)}
        mode={mode}
      />
      <PositionSelectorColumn>
        <PositionSelectorButton
          isSelected={tooltipPosition === Position.Top}
          onClick={() => setPosition(Position.Top)}
          mode={mode}
        />
        <CenterDot />
        <PositionSelectorButton
          isSelected={tooltipPosition === Position.Bottom}
          onClick={() => setPosition(Position.Bottom)}
          mode={mode}
        />
      </PositionSelectorColumn>
      <PositionSelectorButton
        isSelected={tooltipPosition === Position.Right}
        onClick={() => setPosition(Position.Right)}
        mode={mode}
      />
    </PositionSelectorWrapper>
  );
};

interface Props {
  nodeIds: string[];
}

export const TooltipPosition = ({ nodeIds }: Props) => {
  const {
    custom,
    tooltipPosition,
    actions: { setCustom },
  } = useMultiNodes(nodeIds, (node) => ({
    custom: node?.data.custom?.custom ?? {},
    tooltipPosition: (node?.data.custom?.custom?.tooltipPosition ?? Position.Auto) as Position,
  }));

  if (!tooltipPosition) {
    // safeguard
    return null;
  }

  const isAutoActive = tooltipPosition === Position.Auto;

  const setPosition = (newPosition: Position) => {
    const nodeId = nodeIds[0];
    const dom = getDomNodeFromDocument(nodeId);
    if (dom) {
      // @ts-ignore
      dom.dataset.refresh = Date.now();
    }

    setCustom({ custom: { ...custom, tooltipPosition: newPosition } });
  };

  return (
    <Section name="Tooltip position">
      <ValueGroup>
        <Inline space="xs" align="center" fillSpace mb={!isAutoActive ? 'xs' : undefined}>
          <ValueButton isActive={isAutoActive} onClick={() => setPosition(Position.Auto)}>
            Auto
          </ValueButton>
          <ValueButton
            isActive={!isAutoActive}
            onClick={() => isAutoActive && setPosition(Position.Right)}
          >
            Manual
          </ValueButton>
        </Inline>
        {!isAutoActive && (
          <PositionSelector
            selectorComponent={
              <Selector
                tooltipPosition={tooltipPosition}
                setPosition={(newPosition: Position) => setPosition(newPosition)}
              />
            }
            label="Tour Step position"
          />
        )}
      </ValueGroup>

      <ValueGroup>
        <TourStepZindex nodeIds={nodeIds} />
      </ValueGroup>
    </Section>
  );
};
